"use client"

import { chakra, createSlotRecipeContext, HTMLChakraProps, mergeProps, RecipeVariantProps, useFieldContext } from "@chakra-ui/react"
import { forwardRef } from "react"
import { inputRecipe } from "./input.recipe"

const { withProvider, withContext } = createSlotRecipeContext({
  recipe: inputRecipe,
})

interface InputFieldProps extends HTMLChakraProps<"input", RecipeVariantProps<typeof inputRecipe>> {
  type?:
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'file';
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-e2e'?: string;
}
/**
 * @useFor the canonical text input field inside a `<Field.Root>` —
 *   single-line input, validation-aware, paired with `<Field.Label>`
 *   and `<Field.ErrorText>`.
 *
 *   Examples in-tree: every form field across SMS / AIM / Roster
 *   that isn't a select, textarea, search, or specialised type.
 *
 *   For search inputs use `<SearchInput>`; for multi-line use
 *   `<Textarea>`; for color use `<ColorPickerInput>`; for selects
 *   use `<SelectInput>` / `<SelectMulti>`. The raw `<Input>` export
 *   below is for when you need an input outside a `<Field.Root>`
 *   (rare; usually means a layout bug).
 */
const BaseInputField = withProvider<HTMLInputElement, InputFieldProps>("input", "field")

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  function InputField(props, ref) {
    // Consume Field context the way Ark's Field.Input does
    // (mergeProps(useFieldContext()?.getInputProps(), props)), so a
    // sibling <Field.Label> associates without an explicit htmlFor/id
    // pair. Context is optional — outside <Field.Root> this is a no-op.
    // Explicit props win on conflict, which preserves call sites that
    // already wire htmlFor/id by hand (e.g. MappingTable).
    const field = useFieldContext()
    // The context props are plain DOM input props — a strict subset of the
    // Chakra-styled prop surface — so widening them for the merge is safe.
    const fieldProps = (field?.getInputProps() ?? {}) as unknown as InputFieldProps
    const mergedProps = mergeProps(fieldProps, props)
    return <BaseInputField ref={ref} {...mergedProps} />
  },
)

interface InputAddonProps extends HTMLChakraProps<"div"> {
  placement?: 'left' | 'right';
}
export const InputAddon = withContext<HTMLDivElement, InputAddonProps>("div", "addon")

interface InputElementProps extends HTMLChakraProps<"div"> {
  placement?: 'left' | 'right';
}
export const InputElement = withContext<HTMLDivElement, InputElementProps>("div", "element")

export const Input = chakra("input", inputRecipe)
